/**
 * RIM — Transfer-Out Flow (5-step wizard).
 *
 * Wave 5.3 IRA-FE-TRANSFERS-AND-BENEFMOD:
 *   POSTs to /v3/ira/post-transfer-out. The Function:
 *     • Detects penalty-exempt institutions via the Q3 admin-config'd
 *       IraInstitutionRelationship catalog (Raymond James / Verna seeded).
 *     • Resolves the TC sequence from the Q4 admin-config'd
 *       IraTransactionCode catalog (scenario TRANSFER_OUT). Empty catalog
 *       → friendly TC_NOT_CONFIGURED 422 with userVisible=true so this
 *       wizard surfaces a "Transaction code sequence pending — contact admin"
 *       banner in place of a 500.
 *
 * Steps:
 *   1. Source plan summary (humanized title from useIraDetail).
 *   2. Destination institution — autocomplete from IraInstitutionRelationship
 *      catalog + free-form "other" with manual entry for one-off destinations.
 *   3. Amount + full/partial.
 *   4. Review with plain-English summary.
 *   5. Submit → success / error.
 *
 * No GUIDs / Cart IDs / raw codes surface — penalty-exempt status is a Pill,
 * resolved scenario is a friendly label, the TC code is shown only in the
 * confirmation banner alongside the human-readable scenario name.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import {
  Button,
  CardRadioGroup,
  KVPair,
  Pill,
  Stepper,
  WidgetCard,
} from '@/components/primitives';
import { EmptyState } from '@/components/common/EmptyState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useIraDetail } from '../hooks/useIraDetail';
import { useEmitEvent } from '../hooks/useEmitEvent';
import type {
  IraInstitutionRelationship,
  IraTransferErrorCode,
  IraTransferPostedData,
  IraTransferSignatureMode,
  IraTransferDestinationInstitution,
} from '@retail-ops/shared-types';
import '../styles/rim.css';

interface TransferOutDraft {
  /** Catalog code when picked from autocomplete; '' for free-form. */
  destinationCode: string;
  destinationName: string;
  achRouting: string;
  accountNumber: string;
  address: string;
  amount: string;
  isFullTransfer: boolean;
  isDirectTransfer: boolean;
  taxYear: number;
  signatureMode: IraTransferSignatureMode;
  officerNotes: string;
}

const STEPS = [
  { label: 'Source plan' },
  { label: 'Destination institution' },
  { label: 'Amount' },
  { label: 'Review' },
  { label: 'Submit' },
];

/** Friendly inline messages keyed off the server error code. */
const TRANSFER_ERROR_TEXT: Record<IraTransferErrorCode, string> = {
  MISSING_DESTINATION_INSTITUTION:
    'Destination institution name is required.',
  MISSING_DESTINATION_PLAN:
    'Destination plan number is required.',
  MISSING_SOURCE_PLAN:
    'Source plan number is required.',
  SAME_PLAN_TRANSFER:
    'Source and destination plan numbers must differ.',
  MISSING_TRANSFER_REASON:
    'Transfer reason is required.',
  TC_NOT_CONFIGURED:
    'Transaction code sequence pending — contact admin. An administrator must seed the IRA Transaction Codes catalog (scenario TRANSFER_OUT) before this transfer can post.',
  PLAN_INACTIVE:
    'Source plan is not active in Core Director.',
  PLAN_NOT_FOUND:
    'Source plan was not found in Core Director.',
  INVALID_AMOUNT:
    'Transfer amount must be a positive number.',
  MISSING_PLAN_NUMBER:
    'Source plan number is required.',
  MISSING_OFFICER:
    'Officer code is required.',
  POST_FAILED:
    'Core Director rejected the transfer post.',
  INVALID_BODY:
    'The transfer request body was invalid.',
};

export function TransferOutFlow(): JSX.Element {
  const { planNumber } = useParams<{ planNumber: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const client = useApiClient();
  const emit = useEmitEvent();

  const { data: ira, loading: loadingIra, error: iraError } = useIraDetail(planNumber);

  const currentYear = new Date().getFullYear();
  const [step, setStep] = useState<number>(0);
  const [draft, setDraft] = useState<TransferOutDraft>({
    destinationCode: '',
    destinationName: '',
    achRouting: '',
    accountNumber: '',
    address: '',
    amount: '',
    isFullTransfer: false,
    isDirectTransfer: true,
    taxYear: currentYear,
    signatureMode: 'wet',
    officerNotes: '',
  });

  const [catalog, setCatalog] = useState<ReadonlyArray<IraInstitutionRelationship>>([]);
  const [catalogLoading, setCatalogLoading] = useState<boolean>(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitErrorCode, setSubmitErrorCode] = useState<IraTransferErrorCode | null>(null);
  const [postedData, setPostedData] = useState<IraTransferPostedData | null>(null);

  // ── Load admin-config'd institution catalog ─────────────────────────
  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    setCatalogError(null);
    client.retail.config
      .iraInstitutionRelationships()
      .then((res) => {
        if (cancelled) return;
        if ('data' in res && Array.isArray(res.data)) {
          setCatalog(res.data);
        } else {
          setCatalog([]);
        }
        setCatalogLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCatalogError(err instanceof Error ? err.message : 'Failed to load institution catalog.');
        setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  const amountNum = Number.parseFloat(draft.amount);
  const selectedRow = useMemo(
    () => catalog.find((r) => r.code === draft.destinationCode) ?? null,
    [catalog, draft.destinationCode],
  );
  const willBePenaltyExempt = selectedRow?.penaltyExempt === true;

  if (!planNumber) {
    return <EmptyState title="Plan number required" message="Open an IRA plan from RIM before starting a transfer-out." />;
  }
  if (loadingIra) {
    return <div className="rim-flow__loading"><LoadingSpinner label="Loading IRA plan…" size="lg" /></div>;
  }
  if (iraError || !ira) {
    return (
      <EmptyState
        title="Unable to load IRA plan"
        message={iraError ?? 'Core Director did not return plan details.'}
        action={<Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>Back to plan</Button>}
      />
    );
  }

  const canProceed = ((): boolean => {
    switch (step) {
      case 0: return true;
      case 1: return draft.destinationName.trim().length > 0;
      case 2: return Number.isFinite(amountNum) && amountNum > 0;
      case 3: return submitState === 'idle' || submitState === 'error';
      case 4: return false;
      default: return false;
    }
  })();

  const submit = async (): Promise<void> => {
    if (!ira.cifId) {
      setSubmitError('Plan is missing the owner CIF — cannot post transfer.');
      setSubmitErrorCode('MISSING_PLAN_NUMBER');
      setSubmitState('error');
      return;
    }
    if (!user?.officerCode) {
      setSubmitError('Officer code is required to post the transfer.');
      setSubmitErrorCode('MISSING_OFFICER');
      setSubmitState('error');
      return;
    }
    setSubmitState('submitting');
    setSubmitError(null);
    setSubmitErrorCode(null);
    setPostedData(null);

    const destinationInstitution: IraTransferDestinationInstitution = {
      name: draft.destinationName.trim(),
      ...(draft.destinationCode ? { code: draft.destinationCode } : {}),
      ...(draft.achRouting.trim() ? { achRouting: draft.achRouting.trim() } : {}),
      ...(draft.accountNumber.trim() ? { accountNumber: draft.accountNumber.trim() } : {}),
      ...(draft.address.trim() ? { address: draft.address.trim() } : {}),
    };

    try {
      const resp = await client.retail.ira.postTransferOut({
        cif: ira.cifId,
        planAccountNumber: ira.planNumber,
        destinationInstitution,
        amount: amountNum,
        isFullTransfer: draft.isFullTransfer,
        isDirectTransfer: draft.isDirectTransfer,
        taxYear: draft.taxYear,
        signatureMode: draft.signatureMode,
        officerCode: user.officerCode,
        ...(draft.officerNotes.trim() ? { officerNotes: draft.officerNotes.trim() } : {}),
      });

      if (!resp.success) {
        setSubmitErrorCode(resp.code);
        setSubmitError(TRANSFER_ERROR_TEXT[resp.code] ?? resp.error ?? resp.message ?? 'Transfer-out post refused.');
        setSubmitState('error');
        return;
      }

      setPostedData(resp.data);
      emit({
        eventType: 'ira-transfer-out-posted',
        eventCategory: 'cif-account',
        severity: 'warn',
        context: `plan:${ira.planNumber}`,
        payload: {
          planNumber: ira.planNumber,
          destinationName: resp.data.destinationInstitutionName,
          amount: amountNum,
          isFullTransfer: resp.data.isFullTransfer,
          penaltyApplies: resp.data.penaltyApplies,
          penaltyExemptionReason: resp.data.penaltyExemptionReason,
          transactionCode: resp.data.transactionCode,
          scenario: resp.data.scenario,
        },
      });
      setSubmitState('submitted');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Transfer-out post failed.');
      setSubmitErrorCode(null);
      setSubmitState('error');
    }
  };

  return (
    <div className="rim-flow">
      <div className="rim-flow__crumb">
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>Back to plan</Button>
        <h1>IRA transfer-out · {ira.planDescription ?? ira.planCodeRaw} · {ira.planNumber}</h1>
      </div>

      <Stepper steps={STEPS} currentIndex={step} variant="apply" />

      {step === 0 && (
        <WidgetCard title="Source plan" stripe="compliance">
          <div className="rim-kv-grid">
            <KVPair label="Plan" value={`${ira.planDescription ?? ira.planCodeRaw} · ${ira.planNumber}`} />
            <KVPair label="Owner CIF" value={ira.cifId ?? '—'} />
            {ira.balance != null && (
              <KVPair label="Current balance" value={`$${ira.balance.toFixed(2)}`} />
            )}
            {ira.contributionYtd != null && (
              <KVPair label="YTD contributions" value={`$${ira.contributionYtd.toFixed(2)}`} />
            )}
          </div>
          <div className="rim-flow__hint">
            Funds will move to an external institution. Transfer codes resolve from the
            admin-editable IRA Transaction Codes catalog (scenario TRANSFER_OUT).
          </div>
        </WidgetCard>
      )}

      {step === 1 && (
        <WidgetCard title="Destination institution" stripe="compliance">
          {catalogLoading && <LoadingSpinner label="Loading institutions…" />}
          {catalogError && (
            <div className="rim-rmd-banner is-warn" role="alert">
              <Pill variant="warn">Catalog</Pill> {catalogError}
            </div>
          )}
          {!catalogLoading && (
            <CardRadioGroup<string>
              name="rim-transferout-dest"
              selected={draft.destinationCode}
              onChange={(v) => {
                const row = catalog.find((c) => c.code === v);
                setDraft({
                  ...draft,
                  destinationCode: v,
                  destinationName: row?.displayName ?? draft.destinationName,
                });
              }}
              options={[
                ...catalog.map((row) => ({
                  value: row.code,
                  primary: row.displayName,
                  helper: row.penaltyExempt
                    ? 'Penalty-exempt carve-out (admin-config\'d)'
                    : `${row.relationshipType.replace(/_/g, ' ').toLowerCase()}`,
                })),
                {
                  value: '',
                  primary: 'Other institution (free-form)',
                  helper: 'Type the destination details below — penalty calc applies normally.',
                },
              ]}
            />
          )}
          <div className="rim-flow__grid-2">
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Institution name</span>
              <input
                type="text"
                className="rim-benef-field"
                value={draft.destinationName}
                placeholder="e.g. Fidelity, Vanguard, Charles Schwab"
                onChange={(e) => setDraft({ ...draft, destinationName: e.currentTarget.value })}
              />
            </label>
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">ACH routing (optional)</span>
              <input
                type="text"
                className="rim-benef-field"
                value={draft.achRouting}
                placeholder="9-digit routing number"
                onChange={(e) => setDraft({ ...draft, achRouting: e.currentTarget.value })}
              />
            </label>
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Destination account # (optional)</span>
              <input
                type="text"
                className="rim-benef-field"
                value={draft.accountNumber}
                onChange={(e) => setDraft({ ...draft, accountNumber: e.currentTarget.value })}
              />
            </label>
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Address (optional, one line)</span>
              <input
                type="text"
                className="rim-benef-field"
                value={draft.address}
                onChange={(e) => setDraft({ ...draft, address: e.currentTarget.value })}
              />
            </label>
          </div>
          {willBePenaltyExempt && (
            <div className="rim-rmd-banner" role="status">
              <Pill variant="ok">Penalty-exempt</Pill> The selected institution is admin-flagged as
              penalty-exempt. The early-withdrawal penalty calc will be skipped on this transfer.
            </div>
          )}
        </WidgetCard>
      )}

      {step === 2 && (
        <WidgetCard title="Amount" stripe="compliance">
          <div className="rim-flow__grid-3">
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Amount (USD)</span>
              <input
                type="number"
                className="rim-benef-field"
                value={draft.amount}
                min={0}
                step={0.01}
                onChange={(e) => setDraft({ ...draft, amount: e.currentTarget.value })}
              />
            </label>
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Tax year (1099-R)</span>
              <input
                type="number"
                className="rim-benef-field"
                value={draft.taxYear}
                min={1900}
                max={9999}
                onChange={(e) => setDraft({ ...draft, taxYear: Number.parseInt(e.currentTarget.value, 10) || draft.taxYear })}
              />
            </label>
            <label className="rim-flow__field">
              <span className="rim-flow__field-label">Signature mode</span>
              <select
                className="rim-benef-field"
                value={draft.signatureMode}
                onChange={(e) => setDraft({ ...draft, signatureMode: e.currentTarget.value as IraTransferSignatureMode })}
              >
                <option value="wet">Wet signature on file</option>
                <option value="digital">Digital signature</option>
                <option value="none">None</option>
              </select>
            </label>
          </div>
          <div className="rim-flow__grid-2">
            <label className="rim-flow__checkbox">
              <input
                type="checkbox"
                checked={draft.isFullTransfer}
                onChange={(e) => setDraft({ ...draft, isFullTransfer: e.currentTarget.checked })}
              />
              <span>Full transfer (closes the source plan)</span>
            </label>
            <label className="rim-flow__checkbox">
              <input
                type="checkbox"
                checked={draft.isDirectTransfer}
                onChange={(e) => setDraft({ ...draft, isDirectTransfer: e.currentTarget.checked })}
              />
              <span>Direct trustee-to-trustee (no 60-day clock)</span>
            </label>
          </div>
          <label className="rim-flow__field">
            <span className="rim-flow__field-label">Officer notes (optional)</span>
            <input
              type="text"
              className="rim-benef-field"
              value={draft.officerNotes}
              onChange={(e) => setDraft({ ...draft, officerNotes: e.currentTarget.value })}
            />
          </label>
        </WidgetCard>
      )}

      {step === 3 && (
        <WidgetCard title="Review" stripe="compliance">
          <div className="rim-kv-grid">
            <KVPair label="Source plan" value={`${ira.planCodeRaw} · ${ira.planNumber}`} />
            <KVPair label="Destination institution" value={draft.destinationName || '—'} />
            <KVPair label="Amount" value={`$${amountNum.toFixed(2)}`} />
            <KVPair label="Full transfer" value={draft.isFullTransfer ? 'Yes — closes plan' : 'No — partial'} />
            <KVPair label="Direct trustee-to-trustee" value={draft.isDirectTransfer ? 'Yes' : 'No (indirect / 60-day)'} />
            <KVPair label="Tax year" value={draft.taxYear} />
            <KVPair label="Signature mode" value={draft.signatureMode} />
          </div>
          {willBePenaltyExempt && (
            <div className="rim-rmd-banner" role="status">
              <Pill variant="ok">Penalty-exempt</Pill> The destination is admin-flagged as
              penalty-exempt. Early-withdrawal penalty will be skipped.
            </div>
          )}
        </WidgetCard>
      )}

      {step === 4 && (
        <WidgetCard title="Submit transfer" stripe="compliance">
          {submitState === 'idle' && (
            <p className="rim-flow__hint">Click "Submit transfer" to post the transaction.</p>
          )}
          {submitState === 'submitting' && <LoadingSpinner label="Posting transfer…" />}
          {submitError && (
            <div className="rim-rmd-banner is-warn" role="alert">
              {submitErrorCode === 'TC_NOT_CONFIGURED' && (
                <Pill variant="warn">TC catalog empty</Pill>
              )}
              {submitErrorCode && submitErrorCode !== 'TC_NOT_CONFIGURED' && (
                <Pill variant="warn">{submitErrorCode}</Pill>
              )}
              {' '}{submitError}
            </div>
          )}
          {submitState === 'submitted' && postedData && (
            <>
              <div className="rim-rmd-banner" role="status">
                Transfer posted via TaxPlnTranAdd · TC <strong>{postedData.transactionCode}</strong>
                {' '}· scenario <strong>Transfer-out</strong>.
              </div>
              {!postedData.penaltyApplies && (
                <div className="rim-rmd-banner" role="status">
                  <Pill variant="ok">Penalty-exempt</Pill>{' '}
                  {postedData.penaltyExemptionReason ?? 'Admin-config\'d carve-out applied.'}
                </div>
              )}
            </>
          )}
        </WidgetCard>
      )}

      <div className="rim-flow__actions">
        {step > 0 && submitState !== 'submitted' && (
          <Button variant="ghost" onClick={() => setStep(step - 1)}>Back</Button>
        )}
        {step < STEPS.length - 1 && (
          <Button variant="primary" disabled={!canProceed} onClick={() => setStep(step + 1)}>Next</Button>
        )}
        {step === STEPS.length - 1 && submitState !== 'submitted' && (
          <Button
            variant="primary"
            disabled={submitState === 'submitting'}
            onClick={() => { void submit(); }}
          >
            {submitState === 'submitting' ? 'Posting…' : 'Submit transfer'}
          </Button>
        )}
        {submitState === 'submitted' && (
          <Button variant="primary" onClick={() => navigate('..')}>Done</Button>
        )}
      </div>
    </div>
  );
}
